import React, { useState, useEffect, useMemo, useRef } from "react";
import { clickable } from "../../utils/a11y";
import axios from "axios";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import Cookies from "js-cookie";
import Loader from "../../utils/loader";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { groupBy } from "../../utils/groupBy";
import { useDialog } from "../../utils/useDialog";

const NODETYPES = {
  DIAGNOSIS: "DIAGNOSIS",
  PRESCRIPTION_TYPE: "PRESCRIPTION_TYPE",
  CATEGORY: "CATEGORY",
  PRESCRIPTION: "PRESCRIPTION",
};

const ALL_CAREPLANS_VALUE = "ALL";

const PatientEducateRebuilt = (props) => {
  const [viewVal, setViewVal] = useState(0);
  const [nodeList, setNodeList] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  const [activeLoader, setActiveLoader] = useState(false);

  const [activeCarePlan, setActiveCarePlan] = useState(null); // number or "ALL"
  const [treatmentFilter, setTreatmentFilter] = useState("ALL"); // ALL | type label

  // modal states
  const [isPopupOpen, setIsPopupOpen] = useState(false); // treatment details modal
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false); // full plan modal

  const navToProfile = (viewVal) => props.changeView(viewVal);

  // fetchData is async and needs the current request when it resolves, not the
  // value captured when the effect that started it was created.
  const focusTreatmentRef = useRef(props.focusTreatment);
  useEffect(() => {
    focusTreatmentRef.current = props.focusTreatment;
  }, [props.focusTreatment]);

  useEffect(() => {
    if (viewVal === 4 || viewVal === 6) navToProfile(viewVal);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVal]);

  const titles = {
    0: "Home",
    1: "Appointments",
    2: "Messaging",
    3: "Education",
    5: "Prescriptions",
  };

  const fetchData = async () => {
    const patientId = Cookies.get(COOKIE_KEYS.userId);
    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getAllCarePlanByPatient?patientId=${patientId}`;

    setActiveLoader(true);
    try {
      const res = await axios.get(url);
      let allNodes = [];
      res.data.forEach((carePlan, index) => {
        allNodes = [...allNodes, ...buildNodeList(carePlan, index)];
      });

      setNodeList(allNodes);

      // default select first diagnosis care plan — but not when the patient
      // arrived here asking for a specific treatment, or this would overwrite
      // their choice with whichever care plan happens to be first.
      const firstDiagnosis = allNodes.find((n) => n.level === 0);
      if (firstDiagnosis && !focusTreatmentRef.current) {
        setActiveCarePlan(firstDiagnosis.carePlanId);
        setSelectedNode(firstDiagnosis);
      }
    } catch (err) {
    } finally {
      setActiveLoader(false);
    }
  };

  const buildNodeList = (data, carePlanId) => {
    const list = [];
    let idx = 0;

    // Diagnosis node (level 0)
    list.push({
      id: idx,
      name: data.diagName,
      type: NODETYPES.DIAGNOSIS,
      level: 0,
      hidden: false,
      expanded: true,
      parent: null,
      overview: data.diagInfo,
      carePlanId: carePlanId,
      nextCheckIn: data.nextCheckIn || null,
      createdOn: data.createdOn || null, // if backend provides, otherwise null
    });
    idx++;

    const presTypeNameMap = {};
    const prescriptions = data?.carePlan?.presTypes || {};

    Object.keys(prescriptions).forEach((pres) => {
      const name = prescriptions[pres]?.[0]?.type?.name;
      if (prescriptions[pres]?.[0]?.type?.showOverview) presTypeNameMap[pres] = name;
    });

    Object.entries(presTypeNameMap).forEach(([presType, presTypeName]) => {
      if ((prescriptions[presType] || []).length !== 0) {
        // Level 1
        list.push({
          id: idx,
          name: presTypeName,
          type: NODETYPES.PRESCRIPTION_TYPE,
          level: 1,
          hidden: false,
          expanded: false,
          parent: 0,
          overview: null,
          carePlanId: carePlanId,
        });
        const pretype = idx;
        idx++;

        const grouped = groupBy(prescriptions[presType], ({ categoryName }) => categoryName);

        Object.entries(grouped).forEach(([categoryName, items]) => {
          const categoried = categoryName !== "null";
          let cat = null;

          if (categoried) {
            list.push({
              id: idx,
              name: categoryName,
              type: NODETYPES.CATEGORY,
              level: 2,
              hidden: true,
              expanded: false,
              parent: pretype,
              overview: null,
              carePlanId: carePlanId,
            });
            cat = idx;
            idx++;
          }

          items.forEach((pres) => {
            list.push({
              id: idx,
              // Raw presName kept alongside the display name so the care plan can
              // link straight to this entry. The DTO carries no prescription id
              // (PrescriptionDetails has presName/patientLabel but no presId), and
              // the two screens render different labels — the care plan shows the
              // clinical name, this tree shows patientLabel — so the link matches
              // on presName, which both sides read from the same payload.
              presName: pres.presName,
              // Patients read plain English here; the clinical name with its dosing
              // shorthand stays on the provider's order tables.
              name: pres.patientLabel || pres.presName,
              type: NODETYPES.PRESCRIPTION,
              level: categoried ? 3 : 2,
              hidden: true,
              expanded: false,
              parent: categoried ? cat : pretype,
              overview: pres.overview,
              carePlanId: carePlanId,
            });
            idx++;
          });
        });
      }
    });

    return list;
  };

  // ---------- derived data ----------
  const diagnosisNodes = useMemo(() => nodeList.filter((n) => n.level === 0), [nodeList]);

  const activeDiagnosisNode = useMemo(() => {
    if (!diagnosisNodes.length) return null;
    if (activeCarePlan === ALL_CAREPLANS_VALUE) return null;
    return diagnosisNodes.find((d) => d.carePlanId === activeCarePlan) || diagnosisNodes[0] || null;
  }, [diagnosisNodes, activeCarePlan]);

  const prescriptionNodesForView = useMemo(() => {
    if (!nodeList.length) return [];
    if (activeCarePlan === ALL_CAREPLANS_VALUE) {
      return nodeList.filter((n) => n.type === NODETYPES.PRESCRIPTION);
    }
    return nodeList.filter(
      (n) => n.type === NODETYPES.PRESCRIPTION && n.carePlanId === activeCarePlan
    );
  }, [nodeList, activeCarePlan]);

  const getPrescriptionTypeLabel = (presNode) => {
    if (!presNode) return null;
    const parentNode = nodeList.find((n) => n.id === presNode.parent);
    if (!parentNode) return null;

    if (parentNode.type === NODETYPES.PRESCRIPTION_TYPE) return parentNode.name;

    if (parentNode.type === NODETYPES.CATEGORY) {
      const typeNode = nodeList.find((n) => n.id === parentNode.parent);
      return typeNode?.type === NODETYPES.PRESCRIPTION_TYPE ? typeNode.name : null;
    }
    return null;
  };

  const allTypeLabels = useMemo(() => {
    return Array.from(
      new Set(
        prescriptionNodesForView
          .map((p) => getPrescriptionTypeLabel(p))
          .filter(Boolean)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptionNodesForView, nodeList]);

  const filteredPrescriptions = useMemo(() => {
    if (treatmentFilter === "ALL") return prescriptionNodesForView;
    return prescriptionNodesForView.filter((p) => getPrescriptionTypeLabel(p) === treatmentFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptionNodesForView, treatmentFilter, nodeList]);

  // “Prescribed Treatments” count should match expected UI (badge)
  const prescribedList = filteredPrescriptions;

  /**
   * Section headings used across the education content. The authored HTML wraps
   * the heading and its body in a single <strong>, so everything renders bold and
   * the heading doesn't stand out at all. Several older entries also omit the
   * colon, or use an entirely different heading set.
   *
   * Longest-first, so "What this is" can't match inside "What this is for".
   */
  const SECTION_HEADINGS = [
    "What you need to know",
    "What to watch for",
    "Why this helps you",
    "Why this helps",
    "Your action plan",
    "What to expect",
    "What this is",
    "Explanation",
    "Pro tips",
  ];

  /**
   * Rewrites authored overview HTML so each section heading becomes a real
   * heading above its paragraph, instead of bold text running into bold text.
   *
   * Works on the parsed document rather than by regex, so malformed or unexpected
   * markup degrades to being left alone rather than mangled.
   */
  const formatOverviewHtml = (html) => {
    if (!html) return "";
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");

      doc.querySelectorAll("p").forEach((p) => {
        const text = (p.textContent || "").replace(/ /g, " ").trim();
        if (!text) return;

        const heading = SECTION_HEADINGS.find((h) =>
          text.toLowerCase().startsWith(h.toLowerCase())
        );
        if (!heading) return;

        // Take whatever follows the heading, with or without a colon.
        let body = text.slice(heading.length).replace(/^\s*[:\-–—]\s*/, "").trim();

        const wrapper = doc.createElement("div");
        wrapper.className = "edu-section";

        const h = doc.createElement("h4");
        h.className = "edu-section-title";
        h.textContent = heading;
        wrapper.appendChild(h);

        if (body) {
          const body_p = doc.createElement("p");
          body_p.className = "edu-section-body";
          body_p.textContent = body;
          wrapper.appendChild(body_p);
          p.replaceWith(wrapper);
          return;
        }

        // Heading alone in its own paragraph, with the text in the next one. This
        // used to bail out here, which is why some sections kept the authored bold
        // black styling while others were converted and rendered in Sage.
        const next = p.nextElementSibling;
        const nextIsHeading =
          next &&
          SECTION_HEADINGS.some((candidate) =>
            (next.textContent || "").trim().toLowerCase().startsWith(candidate.toLowerCase())
          );

        p.replaceWith(wrapper);
        if (next && next.tagName === "P" && !nextIsHeading) {
          next.classList.add("edu-section-body");
          wrapper.appendChild(next);
        }
      });

      return doc.body.innerHTML;
    } catch {
      return html;
    }
  };

  const getOverviewSnippet = (overview) => {
    if (!overview) return "";
    const text = overview.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (text.length <= 140) return text;
    return text.slice(0, 140) + "…";
  };

  const getReadTime = (overview) => {
    if (!overview) return "3 min read";
    const text = overview.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    const words = text.split(/\s+/).length || 1;
    const minutes = Math.max(3, Math.round(words / 200));
    return `${minutes} min read`;
  };

  // ---------- handlers ----------
  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);
  const treatmentDialogRef = useDialog(isPopupOpen && Boolean(selectedNode), closePopup);

  const openPlanModal = () => setIsPlanModalOpen(true);
  const closePlanModal = () => setIsPlanModalOpen(false);
  const planDialogRef = useDialog(isPlanModalOpen, closePlanModal);

  const handleDiagnosisSelectChange = (e) => {
    const value = e.target.value;

    if (value === ALL_CAREPLANS_VALUE) {
      setActiveCarePlan(ALL_CAREPLANS_VALUE);
      setSelectedNode(null);
      setTreatmentFilter("ALL");
      return;
    }

    const carePlanId = Number(value);
    setActiveCarePlan(carePlanId);

    const diagNode = diagnosisNodes.find((d) => d.carePlanId === carePlanId);
    if (diagNode) {
      setSelectedNode(diagNode);
      setTreatmentFilter("ALL");
    }
  };

  const handleTreatmentClick = (presNode) => {
    setSelectedNode(presNode);
    openPopup();
  };

  // Arriving from a care plan entry: open that specific treatment rather than
  // dropping the patient at the top of the education tab to hunt for it.
  //
  // Both screens fetch the same /getAllCarePlanByPatient payload and carePlanId is
  // just its array index, so a diagnosis name plus a prescription name pins one
  // entry exactly. Scoping by care plan matters — the same drug can appear under
  // more than one diagnosis, and matching on name alone would open the wrong one.
  useEffect(() => {
    const focus = props.focusTreatment;
    if (!focus || nodeList.length === 0) return;

    // Compared case- and whitespace-insensitively on purpose. Diagnosis names are
    // authored in the database and their casing does not match what the UI shows
    // — the seeded record is "Enlarged prostate" against a displayed "Enlarged
    // Prostate" — so an exact comparison found no diagnosis at all.
    const norm = (v) => String(v ?? "").trim().toLowerCase();

    const diagnosisNode = nodeList.find(
      (n) => n.level === 0 && norm(n.name) === norm(focus.diagName)
    );

    // Scoped to the care plan the patient clicked from. Searching every plan when
    // the diagnosis could not be resolved is what made this land on whichever
    // condition happened to be first: the same drug appears under more than one,
    // so every click opened the first match rather than the right one.
    const target = diagnosisNode
      ? nodeList.find(
          (n) =>
            n.type === NODETYPES.PRESCRIPTION &&
            norm(n.presName) === norm(focus.presName) &&
            n.carePlanId === diagnosisNode.carePlanId
        )
      : null;

    if (target) {
      setActiveCarePlan(target.carePlanId);
      setTreatmentFilter("ALL");
      setSelectedNode(target);
      setIsPopupOpen(true);
      return;
    }

    if (diagnosisNode) {
      // Treatment not found, but at least land on the right condition rather than
      // whichever care plan happens to be first.
      setActiveCarePlan(diagnosisNode.carePlanId);
      setSelectedNode(diagnosisNode);
    }

    // The request is deliberately NOT cleared here. fetchData sets a default
    // selection when it resolves, and under StrictMode it runs twice — clearing
    // on the first pass let the second one overwrite the chosen treatment with
    // the first care plan, permanently, because this effect had nothing left to
    // reapply. Leaving it set means a later resolve simply re-selects the right
    // node. PatientView clears it on the next navigation instead.
  }, [props.focusTreatment, nodeList]);

  // ---------- FULL PLAN modal content ----------
  const getTypeNodesForActivePlan = () => {
    if (activeCarePlan === ALL_CAREPLANS_VALUE || activeCarePlan === null) return [];
    return nodeList.filter(
      (n) => n.type === NODETYPES.PRESCRIPTION_TYPE && n.carePlanId === activeCarePlan
    );
  };

  const getChildren = (parentId) =>
    nodeList.filter((n) => n.parent === parentId && n.carePlanId === activeCarePlan);

  const collectPrescriptionsUnderType = (typeNodeId) => {
    const children = getChildren(typeNodeId);

    const result = [];
    children.forEach((child) => {
      if (child.type === NODETYPES.CATEGORY) {
        const catChildren = getChildren(child.id);
        catChildren.forEach((p) => {
          if (p.type === NODETYPES.PRESCRIPTION) result.push(p);
        });
      } else if (child.type === NODETYPES.PRESCRIPTION) {
        result.push(child);
      }
    });

    return result;
  };

  const planSections = useMemo(() => {
    if (!activeDiagnosisNode || activeCarePlan === ALL_CAREPLANS_VALUE) return [];
    const typeNodes = getTypeNodesForActivePlan();

    return typeNodes
      .map((t) => ({
        typeName: t.name,
        items: collectPrescriptionsUnderType(t.id),
      }))
      .filter((s) => s.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDiagnosisNode, activeCarePlan, nodeList]);

  const typeIcon = (typeName) => {
    const name = (typeName || "").toLowerCase();
    if (name.includes("lifestyle")) return "monitor_heart";
    if (name.includes("med")) return "medication";
    if (name.includes("lab")) return "science";
    if (name.includes("supp")) return "local_florist";
    return "description";
  };

  // ---------- UI ----------
  return (
    <div
      className="educate-page"
      style={{
        height: "100vh",
        width: "100%",
        overflow: "hidden", // IMPORTANT: stop body scroll, we scroll content only
      }}
    >
      {/* Header (nav) */}

      {/* Scrollable content BELOW the header */}
      <div
        className="educate-scroll"
        style={{
          height: "95%", // excludes header height area
          overflowY: "auto",
          padding: "0 20px",
        }}
      >
        {/* Centered container with right spacing */}
        <div style={{ margin: "0 12px auto" }}>
          <h1 style={{ margin: "8px 0 4px", color: "var(--youro-charcoal)", fontSize: 28, fontWeight: 500 }}>
            Education Center
          </h1>
          <p style={{ margin: "0 0 22px", color: "var(--youro-muted)", fontSize: 16 }}>
            Educational materials curated based on your prescribed treatments
          </p>

          {/* No data */}
          {(!nodeList || nodeList.length === 0 || (!activeDiagnosisNode && activeCarePlan !== ALL_CAREPLANS_VALUE)) && (
            <div style={{ marginTop: 40 }}>
              <h3 style={{ color: "var(--youro-muted)", fontWeight: 500 }}>No education content available yet.</h3>
              <button
                className="btn-filled"
                onClick={() => props.changeView(1)}
                style={{
                  border: "none",
                  background: "var(--youro-sage)",
                  color: "var(--youro-charcoal)",
                  borderRadius: 999,
                  padding: "10px 18px",
                  cursor: "pointer",
                }}
              >
                Book an Appointment
              </button>
            </div>
          )}

          {nodeList && nodeList.length > 0 && (
            <>
              {/* Summary card ONLY when a specific diagnosis is selected */}
              {activeCarePlan !== ALL_CAREPLANS_VALUE && activeDiagnosisNode && (
                <div
                  style={{
                    background: "var(--youro-card)",
                    borderRadius: 14,
                    padding: "18px 18px",
                    border: "1px solid var(--youro-border)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 18,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 500, color: "var(--youro-charcoal)" }}>
                      {activeDiagnosisNode.name} Care Plan
                    </div>
                    <div style={{ marginTop: 6, fontSize: 14, color: "var(--youro-muted)" }}>
                      {filteredPrescriptions.length} active treatments
                      {activeDiagnosisNode?.nextCheckIn ? (
                        <>
                          {" "}
                          • Next check-in: {activeDiagnosisNode.nextCheckIn}
                        </>
                      ) : null}
                    </div>
                  </div>

                  <button
                    onClick={openPlanModal}
                    style={{
                      borderRadius: 999,
                      padding: "10px 16px",
                      border: "1px solid var(--youro-sky)",
                      background: "var(--youro-card)",
                      color: "var(--youro-sky)",
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    View Full Plan
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                      chevron_right
                    </span>
                  </button>
                </div>
              )}

              {/* Controls row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                {/* Diagnosis dropdown */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16, color: "var(--youro-muted)" }}>Diagnosis:</span>
                  <select
                    value={activeCarePlan ?? ""}
                    onChange={handleDiagnosisSelectChange}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 12,
                      border: "1px solid var(--youro-border)",
                      fontSize: 16,
                      background: "var(--youro-card)",
                      minWidth: 280,
                    }}
                  >
                    {/* ✅ All Diagnoses */}
                    <option value={ALL_CAREPLANS_VALUE}>All Diagnoses</option>

                    {diagnosisNodes.map((d) => (
                      <option key={d.carePlanId} value={d.carePlanId}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filter chips */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => setTreatmentFilter("ALL")}
                    style={{
                      borderRadius: 14,
                      border: treatmentFilter === "ALL" ? "none" : "1px solid var(--youro-border)",
                      padding: "10px 16px",
                      fontSize: 16,
                      cursor: "pointer",
                      background: treatmentFilter === "ALL" ? "var(--youro-sage)" : "var(--youro-sand)",
                      color: "var(--youro-charcoal)",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontWeight: 500,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                      menu_book
                    </span>
                    All
                  </button>

                  {allTypeLabels.map((label) => (
                    <button
                      key={label}
                      onClick={() => setTreatmentFilter(label)}
                      style={{
                        borderRadius: 14,
                        border: "1px solid var(--youro-border)",
                        padding: "10px 16px",
                        fontSize: 16,
                        cursor: "pointer",
                        background: treatmentFilter === label ? "var(--youro-sage)" : "var(--youro-sand)",
                        color: "var(--youro-charcoal)",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontWeight: 500,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                        {typeIcon(label)}
                      </span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Note banner */}
              <div
                style={{
                  background: "var(--youro-mint)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  fontSize: 16,
                  color: "var(--youro-charcoal)",
                  marginBottom: 22,
                  border: "1px solid var(--youro-border)",
                }}
              >
                <strong>Note:</strong> Content is organized based on treatments prescribed by your provider.{" "}
                {activeCarePlan === ALL_CAREPLANS_VALUE ? (
                  <>Showing resources for all your diagnoses.</>
                ) : (
                  <>Showing resources for {activeDiagnosisNode?.name}.</>
                )}
              </div>

              {/* Prescribed Treatments */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 20, fontWeight: 500, color: "var(--youro-charcoal)" }}>Prescribed Treatments</div>
                <span
                  style={{
                    background: "var(--youro-mint)",
                    color: "var(--youro-charcoal)",
                    borderRadius: 12,
                    padding: "2px 10px",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {prescribedList.length}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 18,
                  paddingBottom: 24,
                }}
              >
                {prescribedList.map((p) => (
                  <div
                    key={`${p.carePlanId}-${p.id}`}
                    {...clickable(() => handleTreatmentClick(p))}
                    style={{
                      background: "var(--youro-card)",
                      borderRadius: 14,
                      padding: "18px 18px 16px",
                      border: "1px solid var(--youro-border)",
                      cursor: "pointer",
                      position: "relative",
                      minHeight: 210,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden", // IMPORTANT: prevent overflow outside card
                    }}
                  >
                    {/* top-right badge */}
                    <div
                      style={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        background: "var(--youro-stone)",
                        color: "var(--youro-charcoal)",
                        borderRadius: 14,
                        padding: "6px 12px",
                        fontSize: 14,
                        fontWeight: 500,
                        maxWidth: "70%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={getPrescriptionTypeLabel(p) || ""}
                    >
                      {getPrescriptionTypeLabel(p)}
                    </div>

                    {/* icon */}
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        background: "var(--youro-mint)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 14,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 24, color: "var(--youro-charcoal)" }}>
                        description
                      </span>
                    </div>

                    <div style={{ fontSize: 20, fontWeight: 500, color: "var(--youro-charcoal)", marginBottom: 10 }}>
                      {p.name}
                    </div>

                    <div
                      style={{
                        fontSize: 16,
                        color: "var(--youro-muted)",
                        lineHeight: 1.4,
                        flexGrow: 1,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {getOverviewSnippet(p.overview)}
                    </div>

                    {/* footer */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                      <span
                        style={{
                          border: "1px solid var(--youro-border)",
                          color: "var(--youro-charcoal)",
                          borderRadius: 999,
                          padding: "6px 12px",
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        Article
                      </span>
                      <span style={{ color: "var(--youro-muted)", fontSize: 14 }}>{getReadTime(p.overview)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Treatment details modal */}
      {isPopupOpen && selectedNode && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 18,
          }}
          onClick={closePopup}
        >
          <div
            style={{
              width: "min(760px, 100%)",
              background: "var(--youro-card)",
              borderRadius: 16,
              border: "1px solid var(--youro-border)",
              position: "relative",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
            ref={treatmentDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edu-treatment-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePopup}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 34,
                height: 34,
                borderRadius: 10,
                border: "1px solid var(--youro-border)",
                background: "var(--youro-sand)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Close"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--youro-charcoal)" }}>
                close
              </span>
            </button>

            <div style={{ padding: "18px 20px" }}>
              <h2 id="edu-treatment-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 6px", color: "var(--youro-charcoal)" }}>
                {selectedNode.name}
              </h2>

              <div
                className="edu-overview"
                style={{ fontSize: 16, color: "var(--youro-charcoal)", lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{
                  __html:
                    selectedNode.overview &&
                    selectedNode.overview.trim().replace(/&nbsp;/g, " ").trim()
                      ? sanitizeHtml(formatOverviewHtml(selectedNode.overview))
                      : "<p>No details available</p>",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ✅ Full plan modal (View Full Plan) */}
      {isPlanModalOpen && activeDiagnosisNode && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 18,
          }}
          onClick={closePlanModal}
        >
          <div
            style={{
              width: "min(760px, 100%)",
              background: "var(--youro-sand)",
              borderRadius: 16,
              border: "1px solid var(--youro-border)",
              position: "relative",
              maxHeight: "88vh",
              overflowY: "auto",
            }}
            ref={planDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Full care plan"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closePlanModal}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 34,
                height: 34,
                borderRadius: 10,
                border: "1px solid var(--youro-border)",
                background: "var(--youro-sand)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Close"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--youro-charcoal)" }}>
                close
              </span>
            </button>

            <div style={{ padding: "18px 20px 20px" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--youro-charcoal)" }}>
                {activeDiagnosisNode.name} Care Plan
              </div>
              <div style={{ marginTop: 6, fontSize: 16, color: "var(--youro-muted)" }}>
                Complete details of your prescribed treatment plan
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 18,
                  flexWrap: "wrap",
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: "1px solid var(--youro-border)",
                }}
              >
                <div style={{ fontSize: 15, color: "var(--youro-muted)" }}>
                  Created on{" "}
                  <span style={{ color: "var(--youro-charcoal)", fontWeight: 500 }}>
                    {activeDiagnosisNode.createdOn || "—"}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, color: "var(--youro-muted)" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--youro-charcoal)" }}>
                    history
                  </span>
                  Next Check-in:{" "}
                  <span style={{ color: "var(--youro-charcoal)", fontWeight: 600 }}>
                    {activeDiagnosisNode.nextCheckIn || "—"}
                  </span>
                </div>
              </div>

              {/* Sections */}
              <div style={{ marginTop: 16 }}>
                {planSections.map((section) => (
                  <div key={section.typeName} style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        background: "var(--youro-mint)",
                        borderRadius: 12,
                        padding: "14px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        border: "1px solid var(--youro-border)",
                        fontSize: 18,
                        fontWeight: 600,
                        color: "var(--youro-charcoal)",
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--youro-charcoal)" }}>
                        {typeIcon(section.typeName)}
                      </span>
                      {section.typeName}
                    </div>

                    <div style={{ padding: "10px 6px 2px 18px" }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {section.items.map((it) => (
                          <li key={it.id} style={{ margin: "8px 0", fontSize: 16, color: "var(--youro-charcoal)" }}>
                            {it.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              {/* Requested Labs block if nothing exists (keeps visual parity) */}
              {planSections.length === 0 && (
                <div
                  style={{
                    background: "var(--youro-mint)",
                    borderRadius: 12,
                    padding: "14px 14px",
                    border: "1px solid var(--youro-border)",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "var(--youro-charcoal)",
                    marginTop: 14,
                  }}
                >
                  Requested Labs
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Loader active={activeLoader} />
    </div>
  );
};

export default PatientEducateRebuilt;